import type { ReactElement } from "react"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import UnderlineLinkAtm from "@onestore/hel/dist/components/atoms/UnderlineLinkAtm"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import DomainSearchResultItemsOrg from "@onestore/hel/dist/components/organisms/DomainSearchResultItemsOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import {
  isResponsePending,
  isResponseSuccessful,
} from "@onestore/onestore-store-common/http"
import _t from "@core/i18n"
import BundleRow from "@gatsby-plugin-bonus/components/DomainsBonusBox/BundleRow"
import { DomainsBonusBoxPlaceholder } from "@gatsby-plugin-bonus/components/DomainsBonusBox/index"
import SearchRow from "@gatsby-plugin-bonus/components/DomainsBonusBox/SearchRow"
import type { SelectedBundle } from "@gatsby-plugin-bonus/types"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import url from "~/lib/url"
import type { DomainSearchBundleState } from "~/store/domain-search/selectors"
import { COMPONENT_ID } from "~/types/component"
import type { DomainCheck } from "../../../../../lib/api/domainSearch"

type ContentProps = {
  domainSearchUrl?: boolean
  results: DomainCheck.Result[]
  requestStatus: number
  onBasketAdd: {
    (
      domain: string,
      domainId: number,
      buttonId: string,
      ignoreHack: boolean
    ): void
  }
  basketDomains: string[]
  onNextStepClick?: { (): void } | undefined
  hasProduct: boolean
  bundleDomainsResult?: DomainSearchBundleState[]
  phrase: string
  onDomainSelected?: (domainResult: DomainCheck.Result) => void
  onBundleSelected?: (bundle: SelectedBundle) => void
  selectedFqdns?: string[]
}

function Content({
  requestStatus,
  phrase,
  results,
  basketDomains,
  onBasketAdd,
  onNextStepClick,
  hasProduct,
  domainSearchUrl,
  bundleDomainsResult,
  onDomainSelected,
  onBundleSelected,
  selectedFqdns,
}: ContentProps): ReactElement {
  const result: DomainSearchBundleState | undefined = bundleDomainsResult
    ? bundleDomainsResult[0]
    : undefined
  const isMediumUp = useBreakpoint("medium")
  const { isNettoPriceType } = usePriceTypeContext()

  const searchUrl = phrase
    ? `${url.getDomainSearchUrl()}#d:${phrase}`
    : url.getDomainSearchUrl() || "#"

  const isPartialBundleSelected = result
    ? result.domains.some((domain) => selectedFqdns?.includes(domain.fqdn))
    : false

  const hasBundle =
    result && !result.isPartialBundleInBasket && !isPartialBundleSelected

  if (isResponsePending(requestStatus)) {
    return <DomainsBonusBoxPlaceholder />
  }

  return (
    <>
      {isResponseSuccessful(requestStatus) ? (
        <PushOrg bottomSpace={3}>
          <DomainSearchResultItemsOrg
            id={COMPONENT_ID.DOMAIN_SEARCH_SECTION_SECONDARY}
            bundle={
              hasBundle ? (
                <BundleRow
                  domains={result.domains}
                  isLoading={result.isLoading}
                  buttonId={result.buttonId}
                  pricing={result.pricing}
                  key={result.buttonId}
                  isPartialBundleInBasket={result.isPartialBundleInBasket}
                  buttonDataTestId="add_to_basket_0"
                  data-test-id="featured_domains_0"
                  onDomainSelected={onDomainSelected}
                  onBundleSelected={onBundleSelected}
                  selectedFqdns={selectedFqdns}
                  onNextStepClick={hasProduct ? onNextStepClick : undefined}
                />
              ) : null
            }
            items={
              <>
                {results.map((result, index) => {
                  return (
                    <SearchRow
                      key={`domain-result-${result.plan_id}`}
                      basketDomains={basketDomains}
                      result={result}
                      onClick={onBasketAdd}
                      onNextStepClick={hasProduct ? onNextStepClick : undefined}
                      buttonDataTestId={`add_to_basket_${hasBundle ? index + 1 : index}`}
                      onDomainSelected={onDomainSelected}
                      selectedFqdns={selectedFqdns}
                    />
                  )
                })}
              </>
            }
            dataTestId="suggested_domains_tlds"
          />

          <PushOrg topSpace={1}>
            <TextAtm
              typography="nano1"
              emphasis="medium"
              align={isMediumUp ? "right" : "center"}
            >
              {isNettoPriceType
                ? _t("domainSearch.mainDomainNoTaxFooter")
                : _t("domainSearch.mainDomainTaxFooter")}
            </TextAtm>
          </PushOrg>
        </PushOrg>
      ) : null}

      {domainSearchUrl ? (
        <AlignContentOrg horizontalAlign={isMediumUp ? "right" : "center"}>
          <UnderlineLinkAtm
            href={searchUrl}
            typography="tiny2"
            title={_t("bonus.domainsGoToSearch")}
          >
            {_t("bonus.domainsGoToSearch")}
          </UnderlineLinkAtm>
        </AlignContentOrg>
      ) : null}
    </>
  )
}

export default Content
